import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, ArrowRight, Image } from 'lucide-react';
import Layout from '../components/Layout';
import useTheme from '../hooks/useTheme';
import { getAllBlogPosts } from '../data/blogPosts';

const Blog = () => {
  const { darkMode, toggleDarkMode, cardClass } = useTheme();
  const posts = getAllBlogPosts('pl');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Blog - Porady o paliwach i podróżach po USA | Galony&Litry</title>
        <meta name="description" content="Blog z poradami o tankowaniu w USA, cenach paliwa, przelicznikach i wszystkim co musisz wiedzieć planując road trip po Stanach." />
        <link rel="canonical" href="https://galonyilitry.pl/blog" />
      </Helmet>

      <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode} lang="pl">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 mb-4">
              Blog
            </h1>
            <p className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Porady, aktualności i wszystko co musisz wiedzieć o paliwach w USA
            </p>
          </div>

          {/* Blog Posts List */}
          <div className="grid gap-6">
            {posts.map((post) => (
              <article
                key={post.slug}
                className={`${cardClass} rounded-2xl border overflow-hidden transition-all duration-300 hover:shadow-2xl`}
              >
                <Link to={`/blog/${post.slug}`} className="flex flex-col md:flex-row">
                  {/* Image placeholder */}
                  <div className={`w-full md:w-64 h-48 md:h-auto flex-shrink-0 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'} flex items-center justify-center`}>
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`flex flex-col items-center justify-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <Image size={48} strokeWidth={1} />
                        <span className="text-xs mt-2">Brak grafiki</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-6 text-left">
                    <div className={`flex items-center text-sm mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <Calendar size={14} className="mr-2" />
                      {formatDate(post.date)}
                    </div>

                    <h2 className={`text-xl font-bold mb-2 text-left ${darkMode ? 'text-white hover:text-cyan-400' : 'text-slate-800 hover:text-blue-600'} transition-colors`}>
                      {post.title}
                    </h2>

                    <p className={`mb-4 text-left ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {post.excerpt}
                    </p>

                    <span
                      className={`inline-flex items-center font-medium ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}
                    >
                      Czytaj dalej
                      <ArrowRight size={16} className="ml-1" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {/* Empty State */}
          {posts.length === 0 && (
            <div className={`${cardClass} rounded-2xl border p-12 text-center`}>
              <p className={`text-lg ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Brak wpisów na blogu. Wkrótce pojawią się nowe artykuły!
              </p>
            </div>
          )}
        </div>
      </Layout>
    </>
  );
};

export default Blog;
